import { useCallback, useEffect, useMemo, useState } from 'react'

import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import {
  clampPercent,
  copyToClipboard,
  formatMoney,
  formatQuantity,
  sanitizeFilenamePart,
  toSafeNumber
} from '../../../helpers/utils'
import { getOrderPaymentAttachmentValues } from '../../../order/helpers/orderDetailsHelpers'
import {
  canApproveRefunds,
  canCountRefunds,
  canProcessRefundCodeErp,
  hasRefundHistoryReadAccess
} from '../../../global_context/capabilities/refundCapabilities'
import {
  UI_TEXT_COPY_TABLE,
  UI_TEXT_EM_DASH,
  UI_TEXT_TABLE_COPIED
} from '../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'
import { getRefundLineStatusKey, getRefundLineStatusLabel } from '../../constants/refundLineStatus'
import { getRefundStatusOption } from '../../helpers/refundDisplay'
import {
  APPROVAL_ACTION_REFUND_STATUSES,
  FINALIZED_REFUND_STATUSES,
  NCR_REGISTRATION_REFUND_STATUSES,
  NCR_NUMBER_MAX_LENGTH,
  REFUND_APPROVAL_DETAIL_TAB,
  REFUND_APPROVAL_HISTORY_TAB,
  REFUND_PRODUCTS_TABLE_HEADERS,
  getRefundLineCondition
} from '../../helpers/refundApprovalUtils'
import { buildRefundFields, buildRefundSummaryFields } from '../../show/shared/refundDetailsHelpers'
import { buildRefundApprovalCsvContent } from '../helpers/buildRefundApprovalCsv'
import { getPrimaryBarcode } from '../helpers/refundScanModalUtils'
import { buildRefundApprovalPdf } from '../helpers/refundApprovalPdfBuilder'

const hasFilledValue = (value) => {
  if (value == null) return false
  const normalized = String(value).trim().toLowerCase()
  return (
    normalized !== '' &&
    normalized !== '-' &&
    normalized !== '—' &&
    normalized !== 'nil' &&
    normalized !== 'null' &&
    normalized !== 'undefined'
  )
}

export default function useRefundApprovalDetail({
  activity,
  onRefresh,
  onActionSuccess,
  onActionError
}) {
  const [scanOpen, setScanOpen] = useState(false)
  const [initialLineUid, setInitialLineUid] = useState('')
  const [confirmAction, setConfirmAction] = useState(null)
  const [lineToReject, setLineToReject] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [copyTooltip, setCopyTooltip] = useState(UI_TEXT_COPY_TABLE)
  const [activeTab, setActiveTab] = useState(REFUND_APPROVAL_DETAIL_TAB)
  const [ncrDialogOpen, setNcrDialogOpen] = useState(false)
  const [ncrNumber, setNcrNumber] = useState('')
  const [ncrError, setNcrError] = useState('')

  const canViewHistory = hasRefundHistoryReadAccess()

  const detailTabs = useMemo(() => {
    const tabs = [
      { value: REFUND_APPROVAL_DETAIL_TAB, label: REFUND_APPROVAL_TEXT.tabDetail, icon: 'info' }
    ]
    if (canViewHistory) {
      tabs.push({
        value: REFUND_APPROVAL_HISTORY_TAB,
        label: REFUND_APPROVAL_TEXT.tabHistory,
        icon: 'history'
      })
    }
    return tabs
  }, [canViewHistory])

  useEffect(() => {
    if (!canViewHistory && activeTab === REFUND_APPROVAL_HISTORY_TAB) {
      setActiveTab(REFUND_APPROVAL_DETAIL_TAB)
    }
  }, [activeTab, canViewHistory])

  const details = activity?.details || []
  const processing = activity?.processing || {}
  const processingProgress = processing?.progress || {}

  const processingLines = useMemo(() => {
    const mappedLines = details.map((detail) => {
      const mapped = {
        ...detail,
        original_quantity: detail.original_quantity ?? detail.quantity
      }
      return {
        ...mapped,
        line_status: getRefundLineStatusKey(mapped)
      }
    })

    return mappedLines.sort((left, right) => {
      const leftProcessed = left.line_status === 'pending' ? 0 : 1
      const rightProcessed = right.line_status === 'pending' ? 0 : 1
      return leftProcessed - rightProcessed
    })
  }, [details])

  const fallbackProgress = useMemo(
    () => ({
      verified_lines: processingLines.filter((item) => item.line_status !== 'pending').length,
      total_lines: processingLines.length,
      rejected_lines: processingLines.filter((item) => item.line_status === 'cancelled').length,
      verified_quantity: processingLines.reduce(
        (sum, item) => sum + toSafeNumber(item.verified_quantity),
        0
      ),
      rejected_quantity: processingLines.reduce((sum, item) => {
        if (item.line_status !== 'cancelled') return sum
        return sum + toSafeNumber(item.original_quantity ?? item.quantity)
      }, 0),
      total_quantity: processingLines.reduce(
        (sum, item) => sum + toSafeNumber(item.original_quantity ?? item.quantity),
        0
      )
    }),
    [processingLines]
  )

  const progress = useMemo(() => {
    if (processingLines.length > 0) return fallbackProgress

    return {
      verified_lines: toSafeNumber(processingProgress.verified_lines),
      total_lines: toSafeNumber(processingProgress.total_lines),
      rejected_lines: toSafeNumber(processingProgress.rejected_lines),
      verified_quantity: toSafeNumber(processingProgress.verified_quantity),
      rejected_quantity: toSafeNumber(processingProgress.rejected_quantity),
      total_quantity: toSafeNumber(processingProgress.total_quantity)
    }
  }, [fallbackProgress, processingLines.length, processingProgress])

  const customFields = useMemo(
    () => (Array.isArray(activity?.custom_fields) ? activity.custom_fields : []),
    [activity]
  )
  const summaryFields = useMemo(() => buildRefundSummaryFields(activity), [activity])
  const fieldsToRender = useMemo(
    () => buildRefundFields(activity, customFields),
    [activity, customFields]
  )
  const paymentAttachments = useMemo(
    () => getOrderPaymentAttachmentValues(customFields),
    [customFields]
  )
  const headerData = useMemo(
    () => ({
      ...activity,
      display_created_date: activity?.created_date || activity?.created_at || activity?.collected_at
    }),
    [activity]
  )

  const processedLinesCount = processingLines.filter(
    (line) => line.line_status !== 'pending'
  ).length
  const allLinesProcessed =
    processingLines.length > 0 && processedLinesCount === processingLines.length
  const allLinesRejected =
    processingLines.length > 0 && processingLines.every((line) => line.line_status === 'cancelled')
  const hasApprovableLines = processingLines.some(
    (line) => line.line_status === 'verified' || line.line_status === 'partial'
  )
  const canApproveCount = allLinesProcessed && hasApprovableLines
  const statusOption = getRefundStatusOption(activity)
  const refundStatusKey = activity?.status
  const canUseApprovalActions = APPROVAL_ACTION_REFUND_STATUSES.includes(refundStatusKey)
  const canWrite = canApproveRefunds() && canUseApprovalActions
  const canScan = canCountRefunds() && canUseApprovalActions
  const hasAssignedWarehouse = hasFilledValue(activity?.warehouse_uid)
  const hasRegisteredNcr = hasFilledValue(activity?.ncr_number)
  const canRegisterNcr =
    canProcessRefundCodeErp() &&
    !hasRegisteredNcr &&
    NCR_REGISTRATION_REFUND_STATUSES.includes(refundStatusKey) &&
    hasAssignedWarehouse
  const isFinalized = FINALIZED_REFUND_STATUSES.includes(refundStatusKey)
  const showLineActions = canScan && !isFinalized

  const processingUrl = `${BASE_BACKEND_URL}/refunds/${activity?.uuid}/processing`

  const tableExportRows = useMemo(
    () =>
      processingLines.map((line) => {
        const originalQty = toSafeNumber(line.original_quantity ?? line.quantity)
        const lineTotal = originalQty * toSafeNumber(line.unit_price)

        return [
          line.product_db_ref || UI_TEXT_EM_DASH,
          line.product_name || UI_TEXT_EM_DASH,
          getPrimaryBarcode(line.product_barcode) || UI_TEXT_EM_DASH,
          line.price_unit || line.unit_of_sale || REFUND_APPROVAL_TEXT.defaultUnit,
          line.motive || UI_TEXT_EM_DASH,
          getRefundLineCondition(line),
          formatQuantity(originalQty),
          line.verified_quantity != null ? formatQuantity(line.verified_quantity) : UI_TEXT_EM_DASH,
          formatMoney(toSafeNumber(line.unit_price)),
          formatMoney(lineTotal),
          getRefundLineStatusLabel(line)
        ]
      }),
    [processingLines]
  )

  const tableTsv = useMemo(
    () =>
      [REFUND_PRODUCTS_TABLE_HEADERS, ...tableExportRows].map((row) => row.join('\t')).join('\n'),
    [tableExportRows]
  )

  const pdfFileBase = useMemo(
    () =>
      [
        'Devolucion',
        sanitizeFilenamePart(activity?.salesman_name, 'usuario'),
        sanitizeFilenamePart(activity?.business?.name, 'business'),
        sanitizeFilenamePart(activity?.uuid, REFUND_APPROVAL_TEXT.filenameFallback)
      ].join('_'),
    [activity?.business?.name, activity?.salesman_name, activity?.uuid]
  )

  const progressPercent =
    progress.total_lines > 0
      ? clampPercent(Math.round((progress.verified_lines / progress.total_lines) * 100))
      : 0

  const progressSummaryMetrics = useMemo(
    () => [
      {
        key: 'products',
        icon: 'deployed_code',
        title: REFUND_APPROVAL_TEXT.productsMetricTitle,
        subtitle: REFUND_APPROVAL_TEXT.productsMetricSubtitle,
        current: progress.verified_lines,
        total: progress.total_lines,
        pending: Math.max(progress.total_lines - progress.verified_lines, 0),
        pendingLabel: REFUND_APPROVAL_TEXT.pendingMetricLabel
      },
      {
        key: 'quantity',
        icon: 'inventory_2',
        title: REFUND_APPROVAL_TEXT.quantityMetricTitle,
        subtitle: REFUND_APPROVAL_TEXT.quantityMetricSubtitle,
        current: progress.verified_quantity,
        total: progress.total_quantity,
        pending: progress.rejected_quantity,
        pendingLabel: REFUND_APPROVAL_TEXT.rejectedMetricLabel
      }
    ],
    [progress]
  )

  const confirmSummaryItems = useMemo(
    () => [
      {
        label: REFUND_APPROVAL_TEXT.refundLabel,
        value: activity?.refund_db_ref || activity?.uuid || UI_TEXT_EM_DASH
      },
      {
        label: REFUND_APPROVAL_TEXT.clientLabel,
        value: activity?.client_name || activity?.client_db_ref || UI_TEXT_EM_DASH
      },
      {
        label: REFUND_APPROVAL_TEXT.productsLabel,
        value: `${formatQuantity(progress.verified_lines)}/${formatQuantity(progress.total_lines)}`
      }
    ],
    [activity, progress.total_lines, progress.verified_lines]
  )

  const saveProcessingLines = useCallback(
    async (lines) => {
      setLoading(true)
      try {
        await fetchWithAuth(processingUrl, {
          method: 'PATCH',
          body: JSON.stringify({ lines })
        })
        await onRefresh?.()
      } finally {
        setLoading(false)
      }
    },
    [onRefresh, processingUrl]
  )

  const handleTerminalActionFailure = useCallback(
    async (error) => {
      await onRefresh?.()
      const isConflict =
        typeof error?.message === 'string' && /HTTP error 422/.test(error.message)
      onActionError?.(
        isConflict
          ? REFUND_APPROVAL_TEXT.alreadyFinalizedError
          : REFUND_APPROVAL_TEXT.actionFailedError
      )
      setConfirmAction(null)
      setRejectReason('')
    },
    [onActionError, onRefresh]
  )

  const handleApprove = useCallback(async () => {
    setLoading(true)
    try {
      await fetchWithAuth(
        `${BASE_BACKEND_URL}/refunds/${activity.uuid}/approve`,
        {
          method: 'POST',
          body: JSON.stringify(
            activity.warehouse_uid ? { warehouse_uid: activity.warehouse_uid } : {}
          )
        }
      )
      await onRefresh?.()
      setConfirmAction(null)
      onActionSuccess?.('approve')
    } catch (error) {
      await handleTerminalActionFailure(error)
    } finally {
      setLoading(false)
    }
  }, [activity, handleTerminalActionFailure, onActionSuccess, onRefresh])

  const handleReject = useCallback(async () => {
    setLoading(true)
    try {
      const body = {}
      if (rejectReason.trim()) body.rejection_reason = rejectReason.trim()
      if (activity.warehouse_uid) body.warehouse_uid = activity.warehouse_uid

      await fetchWithAuth(
        `${BASE_BACKEND_URL}/refunds/${activity.uuid}/reject`,
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      )
      await onRefresh?.()
      setConfirmAction(null)
      setRejectReason('')
      onActionSuccess?.('reject')
    } catch (error) {
      await handleTerminalActionFailure(error)
    } finally {
      setLoading(false)
    }
  }, [activity, handleTerminalActionFailure, onActionSuccess, onRefresh, rejectReason])

  const openNcrDialog = useCallback(() => {
    setNcrNumber('')
    setNcrError('')
    setNcrDialogOpen(true)
  }, [])

  const closeNcrDialog = useCallback(() => {
    if (loading) return
    setNcrDialogOpen(false)
    setNcrNumber('')
    setNcrError('')
  }, [loading])

  const handleNcrNumberChange = useCallback((value) => {
    setNcrNumber(value.slice(0, NCR_NUMBER_MAX_LENGTH))
  }, [])

  const handleRegisterNcr = useCallback(async () => {
    const normalizedNcr = ncrNumber.trim().slice(0, NCR_NUMBER_MAX_LENGTH)
    if (!normalizedNcr) {
      setNcrError(REFUND_APPROVAL_TEXT.ncrRequired)
      return
    }

    setLoading(true)
    setNcrError('')
    try {
      await fetchWithAuth(
        `${BASE_BACKEND_URL}/refunds/${activity.uuid}/ncr`,
        {
          method: 'PATCH',
          body: JSON.stringify({ ncr_number: normalizedNcr })
        }
      )
      await onRefresh?.()
      setNcrDialogOpen(false)
      setNcrNumber('')
      onActionSuccess?.('ncr')
    } catch (error) {
      setNcrError(REFUND_APPROVAL_TEXT.ncrSaveError)
    } finally {
      setLoading(false)
    }
  }, [activity?.uuid, ncrNumber, onActionSuccess, onRefresh])

  const handleRejectLine = useCallback(async () => {
    if (!lineToReject?.refund_item_uid) return

    const rejectedLineUid = lineToReject.refund_item_uid
    const willRejectAllLines =
      processingLines.length > 0 &&
      processingLines.every(
        (line) => line.refund_item_uid === rejectedLineUid || line.line_status === 'cancelled'
      )

    await saveProcessingLines([{ refund_item_uid: rejectedLineUid, verified_quantity: 0 }])
    setLineToReject(null)

    if (willRejectAllLines) {
      setRejectReason('')
      setConfirmAction('reject')
    }
  }, [lineToReject, processingLines, saveProcessingLines])

  const openScanModal = useCallback((lineUid = '') => {
    setInitialLineUid(lineUid)
    setScanOpen(true)
  }, [])

  const closeScanModal = useCallback(() => {
    setScanOpen(false)
  }, [])

  const handleCopyTable = useCallback(async () => {
    await copyToClipboard(tableTsv)
    setCopyTooltip(UI_TEXT_TABLE_COPIED)
    window.setTimeout(() => setCopyTooltip(UI_TEXT_COPY_TABLE), 1800)
  }, [tableTsv])

  const handleDownloadTableCsv = useCallback(() => {
    const csvContent = buildRefundApprovalCsvContent({
      activity,
      headerData,
      summaryFields,
      fieldsToRender,
      attachments: paymentAttachments,
      progress,
      tableExportRows
    })
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = objectUrl
    link.download = `${pdfFileBase}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
  }, [
    activity,
    fieldsToRender,
    headerData,
    paymentAttachments,
    pdfFileBase,
    progress,
    summaryFields,
    tableExportRows
  ])

  const handleDownloadTablePdf = useCallback(async () => {
    setIsGeneratingPdf(true)

    try {
      await buildRefundApprovalPdf({
        activity: headerData,
        statusLabel:
          statusOption?.label || activity?.status || UI_TEXT_EM_DASH,
        progress,
        summaryFields,
        extraFields: fieldsToRender,
        attachments: paymentAttachments,
        tableHeaders: REFUND_PRODUCTS_TABLE_HEADERS,
        tableRows: tableExportRows,
        filenameBase: pdfFileBase
      })
    } catch (error) {
      console.error(REFUND_APPROVAL_TEXT.pdfError, error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [
    activity,
    fieldsToRender,
    headerData,
    paymentAttachments,
    pdfFileBase,
    progress,
    statusOption?.label,
    summaryFields,
    tableExportRows
  ])

  const openRejectConfirm = useCallback(() => {
    setRejectReason('')
    setConfirmAction('reject')
  }, [])

  const openApproveConfirm = useCallback(() => {
    setConfirmAction('approve')
  }, [])

  const closeConfirmAction = useCallback(() => {
    setConfirmAction(null)
  }, [])

  const closeRejectConfirm = useCallback(() => {
    setConfirmAction(null)
    setRejectReason('')
  }, [])

  return {
    activeTab,
    setActiveTab,
    detailTabs,
    canViewHistory,
    statusOption,
    headerData,
    summaryFields,
    fieldsToRender,
    paymentAttachments,
    progress,
    progressPercent,
    progressSummaryMetrics,
    processingLines,
    processedLinesCount,
    allLinesRejected,
    canApproveCount,
    canWrite,
    canRegisterNcr,
    isFinalized,
    showLineActions,
    loading,
    isGeneratingPdf,
    copyTooltip,
    scanOpen,
    initialLineUid,
    confirmAction,
    lineToReject,
    setLineToReject,
    ncrDialogOpen,
    ncrNumber,
    ncrError,
    setNcrNumber: handleNcrNumberChange,
    rejectReason,
    setRejectReason,
    confirmSummaryItems,
    openScanModal,
    closeScanModal,
    saveProcessingLines,
    handleApprove,
    handleReject,
    handleRegisterNcr,
    handleRejectLine,
    handleCopyTable,
    handleDownloadTableCsv,
    handleDownloadTablePdf,
    openRejectConfirm,
    openApproveConfirm,
    openNcrDialog,
    closeConfirmAction,
    closeRejectConfirm,
    closeNcrDialog
  }
}
